import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import LayoutContext from "../../../LayoutContext";
import * as Library from "../../../utils/Library";
import { getAllUnlockedNavigations, userIsOnBoardingInPage } from "../../../utils/onboardingHelper";
import { setNavigations } from "../../../utils/redux/navigationAccessSlice";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import AddressFormCardStandard from "../../common/forms/AddressFormCardStandard";
import FormCard from "../../common/forms/FormCard";
import FormField from "../../common/forms/FormField";
import UploadLogosCardStandard from "../../common/forms/UploadLogosCardStandard";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import PopupContext from "../../../PopupContext";

// #region SCOPED CSS
const StyledPage = styled.div`
    .input-remove-default {
        border: none;
        outline: none;
        background: none;
    }

    .selects {
        background: var(--backgrounds-and-lines-n-600, #10141b);
    }

    .selects.mandatory {
        border: 1px solid transparent;
        border-left: 5px solid var(--text-icons-orange-500);
        border-radius: 4px;
        outline: none;
    }

    .selects.mandatory > div {
        border: none;
        cursor: pointer;
    }

    .selects.mandatory.error {
        border: 1px solid var(--text-icons-red-500);
        border-left: 5px solid var(--text-icons-red-500);
    }

    .main-window-header {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        display: flex;
        flex-direction: row;
        row-gap: 0px;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 68px;
        position: relative;
    }
    .frame-40 {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        position: relative;
    }
    .btn-large-solid {
        background: var(--buttons-all-platforms-gray-300, #1e2631);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        row-gap: 0px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 120px;
        height: 35px;
        position: relative;
    }
    .button {
        color: var(--buttons-all-platforms-gray-200, #4f5b6d);
        text-align: left;
        font-family: var(--button-text-small-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--button-text-small-font-size, 10px);
        letter-spacing: var(--button-text-small-letter-spacing, 0.0125em);
        font-weight: var(--button-text-small-font-weight, 400);
        text-transform: uppercase;
        position: relative;
    }
`;

const CardTitleButtonStyled = styled.div`
    &.btn-sm-inverted-card-header,
    &.btn-sm-inverted-card-header * {
        box-sizing: border-box;
    }
    &.btn-sm-inverted-card-header {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        height: 16px;
        position: relative;
        cursor: pointer;
    }
    .button {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-family: "Roboto-Regular", sans-serif;
        font-size: 8px;
        letter-spacing: 0.0125em;
        font-weight: 400;
        text-transform: uppercase;
        position: relative;
    }
`;
// #endregion SCOPED CSS

export default function POSCreate() {
    const location = useLocation();
    const isCreatePage = location.pathname.includes("/create" || "/new" || "/add");
    document.title = `${APP_TITLE} - POS ${isCreatePage ? "Create" : "Edit"}`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { showIsCompanySavedPopup, setShowIsCompanySavedPopup } = useContext(LayoutContext);
    const { setHasUnsavedChanges } = useContext(AppContext);
    const { setShowOnboardingCompletedPopup } = useContext(PopupContext);
    const dispatch = useDispatch();
    const navigations = useSelector(state => state.navigationAccesses);
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData,
        navigations: navigations
    };

    const { posID } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingLogoData, setIsFetchingLogoData] = useState(false);
    const [isFetchingAddressData, setIsFetchingAddressData] = useState(false);
    const [saveBtnDisabled, setSaveBtnDisabled] = useState(true);

    const [posFormValues, setPOSFormValues] = useState({
        posName: "",
        toCompany: "",
        website: "",
        documentationWebsite: "",
        supportEmail: "",
        mainLogo: null,
        compactLogo: null,
        mainLogoWhite: null,
        mainLogoBlack: null,
        compactLogoWhite: null,
        compactLogoBlack: null,
        googlePlusCode: "",
        posCountry: "",
        posCity: "",
        posPostalCode: "",
        posAddressOne: "",
        posAddressTwo: "",
        posLatitude: "",
        posLongitude: ""
    });

    const [logoFieldsState, setLogoFieldsState] = useState();
    const isUserOnboarding = userIsOnBoardingInPage(navigations, "POS");

    const navigate = useNavigate();
    const formPOSID = useRef();
    const formPOSFieldsRefs = [];
    const posAccountLogosRef = useRef();
    const formPOSAddress = useRef();

    const [environments, setEnvironments] = useState([]);

    const [dropDownData] = useState([
        {
            name: localStorage.getItem("currentCompanyName"),
            value: localStorage.getItem("currentOrganizationID"),
            selected: isUserOnboarding ? true : false
        }
    ]);

    const [readyToSave, setShowReadyToSave] = useState(false);
    const [showConfirmNewPOS, setShowConfirmNewPOS] = useState(false);
    const [posData] = useState(JSON.parse(localStorage.getItem("POSs"))?.[0] || {});

    const formFieldPOS = [
        <FormField
            type="text"
            title="POS name"
            tooltip="POS name"
            requiredType="mandatory"
            assistiveText=""
            varName="posName"
            ref={el => (formPOSFieldsRefs.posName = el)}
            value={posFormValues?.posName}
        />,
        <FormField
            type="dropdown"
            dropdownData={dropDownData || []}
            title="To Company"
            tooltip="To Company"
            requiredType="mandatory"
            assistiveText=""
            varName="toCompany"
            ref={el => (formPOSFieldsRefs.toCompany = el)}
            value={posFormValues?.toCompany}
        />,
        <FormField
            type="text"
            title="Website"
            tooltip="Website"
            assistiveText=""
            varName="website"
            requiredType="mandatory"
            ref={el => (formPOSFieldsRefs.website = el)}
            value={posFormValues?.website}
        />,
        <FormField
            type="text"
            title="Documentation website"
            tooltip="Documentation website"
            assistiveText=""
            requiredType="mandatory"
            varName="documentationWebsite"
            ref={el => (formPOSFieldsRefs.documentationWebsite = el)}
            value={posFormValues?.documentationWebsite}
        />,
        <FormField
            type="email"
            title="Support email"
            tooltip="Support email"
            assistiveText=""
            requiredType="mandatory"
            varName="supportEmail"
            placeholder="support@email.com"
            ref={el => (formPOSFieldsRefs.supportEmail = el)}
            value={posFormValues?.supportEmail}
        />,
        <FormField
            type="tags"
            title="Environments"
            tooltip="Environments"
            requiredType="mandatory"
            varName="environments"
            ref={el => (formPOSFieldsRefs.environments = el)}
            value={environments}
        />
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        generateLogoFields(null, null, null, null, null, null);

        if (posID) {
            getPOSData(posID);
        } else if (isUserOnboarding) {
            setPOSFormValues(prevValues => ({
                ...prevValues,
                posName: posData?.name || prevValues?.posName || "",
                website: posData?.website || prevValues?.website || "",
                documentationWebsite: posData?.documentationWebsite || prevValues?.documentationWebsite || "",
                supportEmail: posData?.supportEmail || prevValues?.supportEmail?.[0] || "",
                environments: posData?.environments || prevValues?.environments || []
            }));
        }
    }, []);

    useEffect(() => {
        const saveIsEnabled = !saveBtnDisabled && !isLoading;

        setupHeaders(props, saveIsEnabled, handleReadyToShowBox, isCreatePage, isUserOnboarding);
    }, [props?.layoutType, saveBtnDisabled, isLoading]);

    useEffect(() => {
        checkFormIsValid(false);
    }, [posFormValues]);
    // #endregion

    // #region METHODS
    const handleReadyToShowBox = () => {
        isUserOnboarding || !isCreatePage ? setShowReadyToSave(true) : setShowConfirmNewPOS(true);
    };

    const checkFormIsValid = (highlightValidation = false) => {
        const allPOSIdFieldsAreValid = formPOSID?.current?.allFieldsAreValid(highlightValidation);
        const allPOSLogosAreValid = posAccountLogosRef?.current?.allFieldsAreValid(highlightValidation);
        const allAddressFieldsAreValid = formPOSAddress?.current?.allFieldsAreValid(highlightValidation);

        const allFieldsAreValid = allPOSIdFieldsAreValid && allPOSLogosAreValid && allAddressFieldsAreValid;

        setSaveBtnDisabled(!allFieldsAreValid);
        return allFieldsAreValid;
    };

    const onSaveClicked = async () => {
        const formPOSIDformValues = formPOSID?.current?.getAllFieldValues();
        const formPOSLogoValues = posAccountLogosRef?.current?.getAllFieldValues();
        const formPOSAddressValues = formPOSAddress?.current?.getAllFieldValues();
        const extractTagNames = arr => arr.map(item => item.tagName);

        const POSID = isUserOnboarding ? posData?._id : posID;

        const jsonPostData = {
            POSID: POSID || "",
            name: formPOSIDformValues?.posName || "",
            companyName: formPOSIDformValues?.toCompany || "",
            website: formPOSIDformValues?.website || "",
            documentationWebsite: formPOSIDformValues?.documentationWebsite || "",
            emailSupport: formPOSIDformValues?.supportEmail || "",
            environments: extractTagNames(formPOSIDformValues?.environments || []),
            location: {
                latitude: formPOSAddressValues?.latitude || "",
                longitude: formPOSAddressValues?.longitude || "",
                googlePlusCode: formPOSAddressValues?.googlePlusCode || "",
                countryName: formPOSAddressValues?.country?.name || "",
                city: formPOSAddressValues?.city?.name || "",
                postalCode: formPOSAddressValues?.postalCode || "",
                addressLine1: formPOSAddressValues?.addressOne || "",
                addressLine2: formPOSAddressValues?.addressTwo || "",
                addressNumber: ""
            },
            logos: {
                mainLogoData: formPOSLogoValues?.mainLogoData || "",
                mainLogoWhiteData: formPOSLogoValues?.mainLogoInWhite || "",
                mainLogoBlackData: formPOSLogoValues?.mainLogoInBlack || "",
                alternativeLogoData: formPOSLogoValues?.compactLogoData || "",
                alternativeLogoWhiteData: formPOSLogoValues?.compactLogoInWhite || "",
                alternativeLogoBlackData: formPOSLogoValues?.compactLogoInBlack || ""
            }
        };

        setHasUnsavedChanges(false);

        if (isCreatePage && !isUserOnboarding) createNewPOS(jsonPostData);
        else updatePOS(jsonPostData);
    };

    const createNewPOS = async jsonPostData => {
        const formData = { ...jsonPostData, companyID: localStorage.getItem("currentCompanyID") };
        const res = await Library.makePostRequest("createPOS", formData, false, setIsLoading);

        if (res?.data?.status === 200) {
            navigate("/pos");
            localStorage.setItem("activePOS", parseInt(localStorage.getItem("activePOS")) + 1);

            if (isUserOnboarding) {
                const updatedNavigations = {
                    ...navigations.navigationAccesses,
                    POS: {
                        ...navigations.navigationAccesses.companies,
                        createModeOnly: false
                    }
                };
                dispatch(setNavigations({ navigationAccesses: updatedNavigations }));
            }
            navigate("/pos");

            Library.showToastMessage({
                type: "success",
                title: `POS ${jsonPostData.name} added successfully`
            });
        } else setHasUnsavedChanges(true);
    };

    const updatePOS = async jsonPostData => {
        const res = await Library.makePostRequest("updatePOS", jsonPostData, false, setIsLoading);

        if (res?.data?.status === 200) {
            if (isUserOnboarding) {
                localStorage.setItem("activePOS", parseInt(localStorage.getItem("activePOS")) + 1);

                const updatedNavigations = getAllUnlockedNavigations(navigations.navigationAccesses);
                dispatch(setNavigations({ navigationAccesses: updatedNavigations }));

                setShowOnboardingCompletedPopup(true);
            } else {
                Library.showToastMessage({
                    type: "success",
                    title: `POS ${jsonPostData.name} updated successfully`
                });
            }
            navigate("/pos");
        } else setHasUnsavedChanges(true);
    };

    const generateLogoFields = (
        largeLogoImageData,
        compactLogoData,
        mainLogoInWhiteData,
        mainLogoInBlackData,
        compactLogoInWhiteData,
        compactLogoInBlackData
    ) => {
        const logosState = [
            {
                label: "Main logo",
                varName: "mainLogoData",
                requiredType: "mandatory",
                imageData: largeLogoImageData,
                gridColumnSize: 2
            },
            {
                label: "Compact logo",
                varName: "compactLogoData",
                requiredType: "recommended",
                imageData: compactLogoData,
                gridColumnSize: 2
            },
            {
                label: "Main logo in white",
                varName: "mainLogoInWhite",
                requiredType: "recommended",
                imageData: mainLogoInWhiteData,
                gridColumnSize: 1
            },
            {
                label: "Main logo in black",
                varName: "mainLogoInBlack",
                requiredType: "recommended",
                imageData: mainLogoInBlackData,
                gridColumnSize: 1
            },
            {
                label: "Compact logo in white",
                varName: "compactLogoInWhite",
                requiredType: "recommended",
                imageData: compactLogoInWhiteData,
                gridColumnSize: 1
            },
            {
                label: "Compact logo in black",
                varName: "compactLogoInBlack",
                requiredType: "recommended",
                imageData: compactLogoInBlackData,
                gridColumnSize: 1
            }
        ];

        setLogoFieldsState(logosState);
    };

    const getCompanyData = async type => {
        const res = await Library.makeCommonPostRequest(
            "Company",
            "getCompany",
            { companyID: localStorage.getItem("currentCompanyID") },
            false,
            type === "logos" ? setIsFetchingLogoData : setIsFetchingAddressData
        );

        if (res?.data?.status !== 200) return;

        const companyData = res?.data?.data;
        return companyData;
    };

    const logosSameAsCompany = async () => {
        const companyData = await getCompanyData("logos");
        if (!companyData) return;

        const {
            mainLogoURL,
            compactLogoURL,
            mainLogoWhiteURL,
            mainLogoBlackURL,
            compactLogoWhiteURL,
            compactLogoBlackURL
        } = companyData;

        generateLogoFields(
            mainLogoURL ? { url: mainLogoURL, name: "mainLogo" } : null,
            compactLogoURL ? { url: compactLogoURL, name: "compactLogo" } : null,
            mainLogoWhiteURL ? { url: mainLogoWhiteURL, name: "mainLogoWhite" } : null,
            mainLogoBlackURL ? { url: mainLogoBlackURL, name: "mainLogoBlack" } : null,
            compactLogoWhiteURL ? { url: compactLogoWhiteURL, name: "compactLogoWhite" } : null,
            compactLogoBlackURL ? { url: compactLogoBlackURL, name: "compactLogoBlack" } : null
        );
    };

    const addressSameAsCompany = async () => {
        const companyData = await getCompanyData("address");
        if (!companyData) return;

        const { addressLine1, addressLine2, city, countryValue, googlePlusCode, latitude, longitude, postalCode } =
            companyData?.location;

        setPOSFormValues({
            ...posFormValues,
            googlePlusCode,
            posCountry: countryValue,
            posCity: city,
            posPostalCode: postalCode,
            posAddressOne: addressLine1,
            posAddressTwo: addressLine2,
            posLatitude: latitude,
            posLongitude: longitude
        });
    };

    const getPOSData = async posID => {
        const res = await Library.makePostRequest("getPOSData", { posID }, false, setIsLoading);

        if (res?.data?.status !== 200) return;
        const data = res?.data?.data;

        const { name, companyName, website, documentationWebsite, emailSupport, environments, logos, location } = data;

        const {
            mainLogoURL,
            alternativeLogoURL,
            mainLogoWhiteURL,
            mainLogoBlackURL,
            alternativeLogoWhiteURL,
            alternativeLogoBlackURL
        } = logos;

        const { latitude, longitude, googlePlusCode, countryName, city, postalCode, addressLine1, addressLine2 } =
            location;

        setPOSFormValues({
            posName: name,
            toCompany: companyName,
            website,
            documentationWebsite,
            supportEmail: emailSupport,
            mainLogo: mainLogoURL ? { url: mainLogoURL, name: "mainLogo" } : null,
            compactLogo: alternativeLogoURL ? { url: alternativeLogoURL, name: "compactLogo" } : null,
            mainLogoWhite: mainLogoWhiteURL ? { url: mainLogoWhiteURL, name: "mainLogoWhite" } : null,
            mainLogoBlack: mainLogoBlackURL ? { url: mainLogoBlackURL, name: "mainLogoBlack" } : null,
            compactLogoWhite: alternativeLogoWhiteURL
                ? { url: alternativeLogoWhiteURL, name: "compactLogoWhite" }
                : null,
            compactLogoBlack: alternativeLogoBlackURL
                ? { url: alternativeLogoBlackURL, name: "compactLogoBlack" }
                : null,
            googlePlusCode,
            posCountry: countryName,
            posCity: city,
            posPostalCode: postalCode,
            posAddressOne: addressLine1,
            posAddressTwo: addressLine2,
            posLatitude: latitude,
            posLongitude: longitude
        });

        setEnvironments(environments || []);

        generateLogoFields(
            mainLogoURL ? { url: mainLogoURL, name: "mainLogo" } : null,
            alternativeLogoURL ? { url: alternativeLogoURL, name: "compactLogo" } : null,
            mainLogoWhiteURL ? { url: mainLogoWhiteURL, name: "mainLogoWhite" } : null,
            mainLogoBlackURL ? { url: mainLogoBlackURL, name: "mainLogoBlack" } : null,
            alternativeLogoWhiteURL ? { url: alternativeLogoWhiteURL, name: "compactLogoWhite" } : null,
            alternativeLogoBlackURL ? { url: alternativeLogoBlackURL, name: "compactLogoBlack" } : null
        );
    };

    // #endregion METHODS

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenOverlay show={showIsCompanySavedPopup}>
                <DialogBoxComponent
                    buttonWidth={"50%"}
                    onClickYes={() => setShowIsCompanySavedPopup(false)}
                    title="Company is now saved!"
                    heading="Success!"
                    headingColor="#00BB00"
                    message={
                        "<div style='font-size: 14px; margin: 0 -5px;'>You can now proceed with enrolling a POS</div>"
                    }
                    // useImageHeader={{ src: BOStageBLogo, width: "165px" }}
                    showConfirm={() => setShowIsCompanySavedPopup(false)}
                    showCancel={false}
                    confirmText="CONTINUE"
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={readyToSave}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-blue-500)"
                    title="Ready to save"
                    message="You can still change it later. No worries:)"
                    onClickYes={() => {
                        setShowReadyToSave(false);
                        onSaveClicked();
                    }}
                    loading={isLoading}
                    loadingHeading={"CREATING POS.."}
                    onClickNo={() => {
                        setShowReadyToSave(false);
                    }}
                    cancelText="No"
                    confirmText="Yes"
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={showConfirmNewPOS}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-blue-500)"
                    title="ENROLLING A NEW POS"
                    message={"<div style>Do you agree to create a new POS?</div>"}
                    onClickYes={() => {
                        setShowConfirmNewPOS(false);
                        onSaveClicked();
                    }}
                    loading={isLoading}
                    loadingHeading={"CREATING POS.."}
                    onClickNo={() => {
                        setShowConfirmNewPOS(false);
                    }}
                    cancelText="No"
                    confirmText="Yes"
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={isLoading}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    heading="SAVING..."
                    showCancel={false}
                    showConfirm={false}
                    loading={isLoading}
                />
            </FullScreenOverlay>

            <div className="flex flex-dir-column gap-16 w-100">
                <FormCard
                    title="POS ID"
                    ref={formPOSID}
                    numOfFieldsPerLine={4}
                    showDefaultRightLegends={true}
                    formFields={formFieldPOS}
                    formFieldRefs={formPOSFieldsRefs}
                    onFormChanged={checkFormIsValid}
                    showUnsavedChangesPopup={true}
                />

                <UploadLogosCardStandard
                    cardTitle="POS LOGOS"
                    key="posLogos"
                    ref={posAccountLogosRef}
                    logoFields={logoFieldsState}
                    onLogoFieldsChanged={() => checkFormIsValid(false)}
                    afterTitleComponent={
                        <CardTitleButton
                            title="SAME AS COMPANY"
                            onClick={logosSameAsCompany}
                            isFetchingData={isFetchingLogoData}
                        />
                    }
                />

                <AddressFormCardStandard
                    cardTitle="POS MAIN OFFICE"
                    ref={formPOSAddress}
                    pinLabel={posFormValues?.posName}
                    unnamedPinLabel={"Unnamed POS"}
                    useGoogleMapsDarkMode={true}
                    hasKYCAssistiveText={false}
                    onFormChanged={checkFormIsValid}
                    locationDetails={{
                        googlePlusCode: posFormValues.googlePlusCode,
                        country: posFormValues.posCountry,
                        city: posFormValues.posCity,
                        postalCode: posFormValues.posPostalCode,
                        addressOne: posFormValues.posAddressOne,
                        addressTwo: posFormValues.posAddressTwo,
                        latitude: posFormValues.posLatitude,
                        longitude: posFormValues.posLongitude
                    }}
                    afterTitleComponent={
                        <CardTitleButton
                            title="SAME AS COMPANY"
                            onClick={addressSameAsCompany}
                            isFetchingData={isFetchingAddressData}
                        />
                    }
                    onLocationDetailsChange={locationDetails => {
                        const {
                            googlePlusCode,
                            country,
                            city,
                            postalCode,
                            addressOne,
                            addressTwo,
                            latitude,
                            longitude
                        } = locationDetails;

                        setPOSFormValues({
                            ...posFormValues,
                            googlePlusCode,
                            posCountry: country,
                            posCity: city,
                            posPostalCode: postalCode,
                            posAddressOne: addressOne,
                            posAddressTwo: addressTwo,
                            posLatitude: latitude,
                            posLongitude: longitude
                        });
                    }}
                />
            </div>
        </StyledPage>
    );
    // #endregion
}

// #region SMALL COMPONENTS AND METHODS
function setupHeaders(props, saveButtonEnabled, onBtnClicked, isCreatePage, userIsOnboarding) {
    const createMode = props?.navigations?.navigationAccesses?.POS?.createModeOnly;

    props.setControlBarLeftBtns([]);
    props.setControlBarCenterBtns([
        { to: "/pos", active: true, disabled: false, title: "POS" },
        { to: "/pos/setup", active: false, disabled: false, title: "SETUP" },
        { to: "/pos/connectfields", active: false, disabled: false, title: "CONNECT FIELDS" },
        { to: "/pos/auth", active: false, disabled: false, title: "AUTH" }
    ]);

    if (isCreatePage || userIsOnboarding) {
        props.setControlBarRightBtns([
            { to: "/pos", active: false, disabled: createMode, title: "View", icon: "eye" },
            { to: "/pos/create", active: true, disabled: false, title: "Add", icon: "plus" },
            { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
        ]);
    } else {
        props.setControlBarRightBtns([
            { to: "/pos", active: false, disabled: false, title: "Back", icon: "chevron-left" },
            { to: "#", active: true, disabled: false, title: "Edit", icon: "pencil-line" },
            { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
        ]);
    }

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader btnTitle="SAVE" btnEnabled={saveButtonEnabled} onClick={onBtnClicked} />
        )
    });
}

export const CardTitleButton = ({ title, onClick, isFetchingData = false }) => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px"
            }}>
            <CardTitleButtonStyled className="btn-sm-inverted-card-header" onClick={onClick}>
                <div className="button">{title}</div>
            </CardTitleButtonStyled>
            {isFetchingData && <i className="fa fa-spinner fa-spin" style={{ color: "var(--text-icons-green-500)" }} />}
        </div>
    );
};
// #endregion
